enum MortgageType {
  MORTGAGE_STANDARD
  MORTGAGE_BUY_TO_LET
  MORTGAGE_RIGHT_TO_BUY
  MORTGAGE_SHARED_OWNERSHIP
  MORTGAGE_SELF_BUILD
  MORTGAGE_SHARED_EQUITY
  MORTGAGE_SECURED_LOAN
  MORTGAGE_SECURED_LOAN_BTL
  MORTGAGE_BRIDGING_LOAN
  MORTGAGE_COMMERCIAL
  MORTGAGE_LET_TO_BUY
  MORTGAGE_STANDARD_OR_SHARED_EQUITY
  MORTGAGE_SECURED_LOAN_LET_TO_BUY
  MORTGAGE_SECURED_LOAN_SHARED_OWNERSHIP
  MORTGAGE_EQUITY_RELEASE
  INVALID_MORTGAGE_TYPE
}

enum MortgageEmploymentStatus {
  EMPLOYMENT_EMPLOYED
  EMPLOYMENT_FIXED_TERM_CONTRACTOR
  EMPLOYMENT_FIXED_TERM_CONTRACTOR_PAYE
  EMPLOYMENT_HOMEMAKER
  EMPLOYMENT_RETIRED
  EMPLOYMENT_SELF_EMPLOYED
}

enum Nationality {
  NATIONALITY_BRITISH
  NATIONALITY_EU_CITIZEN
  NATIONALITY_NON_EU_CITIZEN
  NATIONALITY_EX_PATRIATE
}

enum SortingColumn {
  INVALID_COLUMN
  COLUMN_INITIAL_PAY_RATE
  COLUMN_INITIAL_MONTHLY_PAYMENT
  COLUMN_TRUE_COST
  COLUMN_TRUE_COST_OVER_INITIAL_PERIOD
}

enum SortingOrder {
  ASCENDING
  DESCENDING
  INVALID_DIRECTION
}

type Applicant {
  employment_status: MortgageEmploymentStatus!
  nationality: Nationality!
}

input ApplicantInput {
  employment_status: MortgageEmploymentStatus!
  nationality: Nationality!
}

type Sorting {
  column: SortingColumn
  direction: SortingOrder
}

input SortingInput {
  column: SortingColumn
  direction: SortingOrder
}

enum MortgageSourcingReason {
  REASON_PURCHASE
  REASON_REMORTGAGE
  INVALID_REASON_FOR_MORTGAGE
}

enum PaymentMethod {
  INVALID_PAYMENT_METHOD
  PAYMENT_METHOD_REPAYMENT
  PAYMENT_METHOD_ENDOWMENT
  PAYMENT_METHOD_INDIVIDUAL_SAVINGS_ACCOUNT
  PAYMENT_METHOD_INTEREST_ONLY
  PAYMENT_METHOD_PART_INVESTMENT
  PAYMENT_METHOD_PENSION
  PAYMENT_METHOD_SALE_OF_PROPERTY
  PAYMENT_METHOD_SALE_OF_OTHER_PROPERTY
}

enum TenantStatus {
  INVALID_TENANT_STATUS
  TENANT_DSS
  TENANT_STUDENT
  TENANT_UNSPECIFIED
}

enum LandlordStatus {
  INVALID_LANDLORD_STATUS
  LANDLORD_FIRST_TIME_WITHOUT_RESIDENTIAL_PROPERTY
  LANDLORD_FIRST_TIME_WITH_RESIDENTIAL_PROPERTY
  LANDLORD_EXISTING_WITHOUT_RESIDENTIAL_PROPERTY
  LANDLORD_EXISTING_WITH_RESIDENTIAL_PROPERTY
}

input MortgageSourcingInput {
  term: String
  term_unit: String
  mortgage_type: MortgageType
  loan_required: String
  expected_valuation: String
  filter_product_term_period_from_months: String
  filter_product_term_period_to_months: String
  applicants: [ApplicantInput]
  mortgage_class_fixed: Boolean!
  mortgage_class_tracker: Boolean!
  mortgage_class_libor_linked: Boolean!
  mortgage_class_discount: Boolean!
  mortgage_class_variable: Boolean!
  mortgage_class_capped: Boolean!
  filter_direct: Boolean!
  filter_ccjs_acceptable: Boolean!
  filter_arrears_acceptable: Boolean!
  filter_bankruptcy_acceptable: Boolean!
  filter_defaults_acceptable: Boolean!
  filter_free_legal_fees: Boolean!
  filter_free_lender_fees: Boolean!
  filter_valuation_fee: Boolean!
  filter_guarantors: Boolean!
  filter_is_offset: Boolean!
  filter_overpayments_allowed: Boolean!
  filter_higher_lending_charge: Boolean!
  filter_sort_results_by_column: SortingInput!
  filter_cashback: Boolean!
  filter_portable: Boolean!
  filter_no_arrangement_fee: Boolean!
  filter_no_booking_fee: Boolean!
  filter_refunded_valuation_fees: Boolean!
  filter_refunded_legal_fees: Boolean!
  filter_ivas_acceptable: Boolean!
  filter_dmps_acceptable: Boolean!
  filter_payday_loans_acceptable: Boolean!
  filter_help_to_buy: Boolean!
  true_cost_disable_add_fees_payable_to_true_cost_total: Boolean!
  true_cost_disable_add_fees_that_will_be_added_to_loan: Boolean!
  true_cost_disable_deduct_cashback: Boolean!
  true_cost_disable_deduct_refunded_fees: Boolean!
  true_cost_disable_calculate_over_initial_period: Boolean!
  true_cost_calculate_over_no_of_months: Int
  reason_for_mortgage: MortgageSourcingReason!
  payment_method: PaymentMethod
  part_investment_loan_required_investment_part: Int
  buy_to_let_expected_rental_income_per_month: Int
  buy_to_let_number_of_properties_owned_excluding_own_residence: Int
  buy_to_let_total_borrowing_on_properties_excluding_own_residence: Int
  buy_to_let_number_of_floors: Int
  buy_to_let_number_of_tenants: Int
  buy_to_let_tenant_status: TenantStatus
  buy_to_let_corporate_let: Boolean
  buy_to_let_house_of_multiple_occupancy: Boolean
  buy_to_let_bedsit: Boolean
  buy_to_let_bedsit_number_of_rooms: Int
  buy_to_let_limited_company: Boolean
  buy_to_let_limited_company_spv: Boolean
  buy_to_let_landlord_status: LandlordStatus
  buy_to_let_consumer: Boolean
  buy_to_let_consumer_regulated: Boolean
  buy_to_let_limited_company_name: String
  buy_to_let_applicants_are_high_tax_rate_payers: Boolean
  buy_to_let_allows_top_slicing: Boolean
  exclude_retention: Boolean
}

type SourceDetails {
  term: Int
  term_unit: TermUnit
  mortgage_type: MortgageType
  loan_required: Int
  expected_valuation: Int
  filter_product_term_period_from_months: Int
  filter_product_term_period_to_months: Int
  applicants: [Applicant]
  mortgage_class_fixed: Boolean
  mortgage_class_tracker: Boolean
  mortgage_class_libor_linked: Boolean
  mortgage_class_discount: Boolean
  mortgage_class_variable: Boolean
  mortgage_class_capped: Boolean
  filter_direct: Boolean
  filter_ccjs_acceptable: Boolean
  filter_arrears_acceptable: Boolean
  filter_bankruptcy_acceptable: Boolean
  filter_defaults_acceptable: Boolean
  filter_free_legal_fees: Boolean
  filter_free_lender_fees: Boolean
  filter_valuation_fee: Boolean
  filter_guarantors: Boolean
  filter_is_offset: Boolean
  filter_overpayments_allowed: Boolean
  filter_higher_lending_charge: Boolean
  filter_sort_results_by_column: Sorting
  filter_cashback: Boolean
  filter_portable: Boolean
  filter_no_arrangement_fee: Boolean
  filter_no_booking_fee: Boolean
  filter_refunded_valuation_fees: Boolean
  filter_refunded_legal_fees: Boolean
  filter_ivas_acceptable: Boolean
  filter_dmps_acceptable: Boolean
  filter_payday_loans_acceptable: Boolean
  true_cost_disable_add_fees_payable_to_true_cost_total: Boolean
  true_cost_disable_add_fees_that_will_be_added_to_loan: Boolean
  true_cost_disable_deduct_cashback: Boolean
  true_cost_disable_deduct_refunded_fees: Boolean
  true_cost_disable_calculate_over_initial_period: Boolean
  true_cost_calculate_over_no_of_months: Int
  reason_for_mortgage: MortgageSourcingReason
  payment_method: PaymentMethod
  part_investment_loan_required_investment_part: Int
  buy_to_let_expected_rental_income_per_month: Int
  buy_to_let_number_of_properties_owned_excluding_own_residence: Int
  buy_to_let_total_borrowing_on_properties_excluding_own_residence: Int
  buy_to_let_number_of_floors: Int
  buy_to_let_number_of_tenants: Int
  buy_to_let_tenant_status: TenantStatus
  buy_to_let_corporate_let: Boolean
  buy_to_let_house_of_multiple_occupancy: Boolean
  buy_to_let_bedsit: Boolean
  buy_to_let_bedsit_number_of_rooms: Int
  buy_to_let_limited_company: Boolean
  buy_to_let_limited_company_spv: Boolean
  buy_to_let_landlord_status: LandlordStatus
  buy_to_let_consumer: Boolean
  buy_to_let_consumer_regulated: Boolean
  buy_to_let_limited_company_name: String
  buy_to_let_applicants_are_high_tax_rate_payers: Boolean
  buy_to_let_allows_top_slicing: Boolean
  exclude_retention: Boolean
}

type SourcedMortgageProduct {
  product_code: ID!
  product_name: String
  lender_code: String
  lender_name: String
  initial_pay_rate: Int
  initial_rate_period_months: String
  mortgage_class: String
  apr_lenders: String
  max_ltv_available: String
  standard_variable_rate: Float
  cashback: String
  valuation_fee: String
  booking_fee: String
  chaps_fee: String
  fees_total: String
  early_repayment_charge_applies_until: String
  early_repayment_charge_applies_until_calculated_date: String
  mortgage_discharge_fee: String
  lender_product_reference: String
  legal_fee: String
  product_type: String
  product_match_status: String
  max_loan_available: String
  true_cost_full_term: String
  initial_monthly_payment: String
}

type Product {
  product_name: String
  max_overpayment_percent_no_erc: String
  early_repayment_charge_applies: String
  arrangement_fee: String
  legal_fee: String
  lender_tel_no: String
  lender_web_address: String
}
